import React, { useState } from 'react';
import { Card, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import HoverCard from './HoverCard';

const { Title } = Typography;

interface HeatmapTableProps {
  returnsAnalysis: any;
}

interface TableData {
  key: string;
  rank: string;
  forward10: { value: number; color: string; backgroundColor: string };
  forward20: { value: number; color: string; backgroundColor: string };
  forward50: { value: number; color: string; backgroundColor: string };
  forward100: { value: number; color: string; backgroundColor: string };
}

const HeatmapTable: React.FC<HeatmapTableProps> = ({ returnsAnalysis }) => {
  const { t } = useTranslation();
  const [hoverData, setHoverData] = useState<{
    data: any;
    rankGroup: string;
    period: string;
    visible: boolean;
    position: { x: number; y: number };
  }>({
    data: null,
    rankGroup: '',
    period: '',
    visible: false,
    position: { x: 0, y: 0 }
  });

  // 根据胜率确定颜色和背景色
  const getWinRateStyle = (rate: number): { color: string; backgroundColor: string } => {
    if (rate >= 70) {
      return { color: '#ffffff', backgroundColor: '#52c41a' }; // 深绿色
    } else if (rate >= 60) {
      return { color: '#ffffff', backgroundColor: '#73d13d' }; // 绿色
    } else if (rate >= 50) {
      return { color: '#000000', backgroundColor: '#bae637' }; // 浅绿色
    } else if (rate >= 40) {
      return { color: '#000000', backgroundColor: '#faad14' }; // 黄色
    } else if (rate >= 30) {
      return { color: '#ffffff', backgroundColor: '#ff7a45' }; // 橙色
    } else {
      return { color: '#ffffff', backgroundColor: '#ff4d4f' }; // 红色
    }
  };

  // 准备表格数据
  const prepareTableData = (): TableData[] => {
    if (!returnsAnalysis?.returns_analysis) return [];

    const rankGroups = ['top_3', 'top_10', 'top_20', 'top_50', 'top_100'];
    const forwardPeriods = ['forward_10', 'forward_20', 'forward_50', 'forward_100'];

    return rankGroups.map((rankGroup) => {
      const rankLabel = rankGroup.replace('top_', 'Top ');
      const rowData: TableData = {
        key: rankGroup,
        rank: rankLabel,
        forward10: { value: 0, color: '#ffffff', backgroundColor: '#ff4d4f' },
        forward20: { value: 0, color: '#ffffff', backgroundColor: '#ff4d4f' },
        forward50: { value: 0, color: '#ffffff', backgroundColor: '#ff4d4f' },
        forward100: { value: 0, color: '#ffffff', backgroundColor: '#ff4d4f' }
      };

      forwardPeriods.forEach((period) => {
        const periodData = returnsAnalysis.returns_analysis[period];
        if (periodData && periodData[rankGroup]) {
          const winRate = periodData[rankGroup].win_rate || 0;
          const columnKey = period.replace('forward_', 'forward') as keyof TableData;
          const style = getWinRateStyle(winRate);
          (rowData[columnKey] as any) = {
            value: winRate,
            ...style
          };
        }
      });

      return rowData;
    });
  };

  const tableData = prepareTableData();

  // 处理鼠标悬停事件
  const handleMouseEnter = (event: React.MouseEvent, rankGroup: string, period: string) => {
    const periodKey = `forward_${period}`;
    const periodData = returnsAnalysis?.returns_analysis?.[periodKey];
    const data = periodData?.[rankGroup];
    
    if (data) {
      setHoverData({
        data,
        rankGroup: rankGroup.replace('top_', 'Top '),
        period,
        visible: true,
        position: { x: event.clientX, y: event.clientY }
      });
    }
  };

  const handleMouseLeave = () => {
    setHoverData(prev => ({ ...prev, visible: false }));
  };

  // 自定义单元格渲染
  const renderCell = (value: { value: number; color: string; backgroundColor: string }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: value.backgroundColor,
      color: value.color,
      padding: '8px 12px',
      borderRadius: '6px',
      fontWeight: 'bold',
      fontSize: '14px',
      minWidth: '60px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }}>
      {value.value.toFixed(0)}%
    </div>
  );

  const columns = [
    {
      title: t('analysis.similarityRank'),
      dataIndex: 'rank',
      key: 'rank',
      width: 120,
      render: (text: string) => (
        <div style={{ 
          fontWeight: 'bold', 
          color: '#1890ff',
          fontSize: '14px'
        }}>
          {text}
        </div>
      )
    },
    {
      title: '10 K-lines',
      dataIndex: 'forward10',
      key: 'forward10',
      width: 100,
      render: (value: { value: number; color: string; backgroundColor: string }, record: any) => (
        <div
          onMouseEnter={(e) => handleMouseEnter(e, record.key, '10')}
          onMouseLeave={handleMouseLeave}
        >
          {renderCell(value)}
        </div>
      )
    },
    {
      title: '20 K-lines',
      dataIndex: 'forward20',
      key: 'forward20',
      width: 100,
      render: (value: { value: number; color: string; backgroundColor: string }, record: any) => (
        <div
          onMouseEnter={(e) => handleMouseEnter(e, record.key, '20')}
          onMouseLeave={handleMouseLeave}
        >
          {renderCell(value)}
        </div>
      )
    },
    {
      title: '50 K-lines',
      dataIndex: 'forward50',
      key: 'forward50',
      width: 100,
      render: (value: { value: number; color: string; backgroundColor: string }, record: any) => (
        <div
          onMouseEnter={(e) => handleMouseEnter(e, record.key, '50')}
          onMouseLeave={handleMouseLeave}
        >
          {renderCell(value)}
        </div>
      )
    },
    {
      title: '100 K-lines',
      dataIndex: 'forward100',
      key: 'forward100',
      width: 100,
      render: (value: { value: number; color: string; backgroundColor: string }, record: any) => (
        <div
          onMouseEnter={(e) => handleMouseEnter(e, record.key, '100')}
          onMouseLeave={handleMouseLeave}
        >
          {renderCell(value)}
        </div>
      )
    }
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={5} style={{ textAlign: 'center', marginBottom: 16 }}>
        🔥 {t('analysis.winRateHeatmap')}
      </Title>
      
      <Card size="small" style={{ borderRadius: 8 }}>
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={false}
          size="small"
          style={{ 
            backgroundColor: '#fafafa',
            borderRadius: 8
          }}
        />
        
        {/* 颜色图例 */}
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 16,
          fontSize: '12px',
          flexWrap: 'wrap'
        }}>
          {/* {[
            { range: '≥70%', color: '#52c41a', textColor: '#ffffff' },
            { range: '60-70%', color: '#73d13d', textColor: '#ffffff' },
            { range: '50-60%', color: '#bae637', textColor: '#000000' },
            { range: '40-50%', color: '#faad14', textColor: '#000000' },
            { range: '30-40%', color: '#ff7a45', textColor: '#ffffff' },
            { range: '<30%', color: '#ff4d4f', textColor: '#ffffff' }
          ].map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: 20,
                height: 20,
                backgroundColor: item.color,
                color: item.textColor,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                marginRight: 4
              }}>
                {item.range}
              </div>
            </div>
          ))} */}
        </div>
      </Card>

      {/* 悬停卡片 */}
      <HoverCard
        data={hoverData.data}
        rankGroup={hoverData.rankGroup}
        period={hoverData.period}
        visible={hoverData.visible}
        position={hoverData.position}
      />
    </div>
  );
};

export default HeatmapTable; 